import type { BeforeErrorHook, BeforeRequestHook, Options } from 'ky';
import { API_URL } from '../../config/env';
import i18n from '../../config/i18n';

/**
 * beforeRequest hook shared by every ky client: forwards the active UI language
 * so the backend can localise async copy (emails, error messages).
 */
export const acceptLanguageHook: BeforeRequestHook = ({ request }) => {
  request.headers.set('Accept-Language', i18n.language || 'it');
};

/**
 * beforeError hook shared by every ky client: attaches the parsed response body
 * to the thrown error so callers can map backend error codes.
 *
 * ky 2.x pre-parses the body into `error.data` (JSON for JSON content-type,
 * plain text otherwise, undefined on parse failure) and consumes the body
 * stream, so `response.clone().json()` no longer works. We mirror `error.data`
 * into `responseBody` to keep the existing error.utils.ts contract unchanged.
 */
export const attachResponseBodyHook: BeforeErrorHook = ({ error }) => {
  const enriched = error as Error & { data?: unknown; responseBody?: unknown };
  if (enriched.data !== undefined) {
    enriched.responseBody = enriched.data;
  }
  return error;
};

export interface BaseKyConfigParams {
  /** Request timeout in ms. */
  timeout: number;
  /** Auth/context hooks injected before the request fires (Clerk token, inspection token, …). */
  authHooks?: BeforeRequestHook[];
}

/**
 * Builds the ky `Options` shared by all clients (prefix, no retry, Accept-Language,
 * JSON error-body extraction). Callers add their own auth hooks and afterResponse logic.
 */
export function buildBaseKyConfig({ timeout, authHooks = [] }: BaseKyConfigParams): Options {
  return {
    prefix: API_URL,
    timeout,
    retry: { limit: 0 },
    hooks: {
      beforeRequest: [acceptLanguageHook, ...authHooks],
      beforeError: [attachResponseBodyHook],
    },
  };
}
